import { useCallback } from 'react';
import { ApolloError, Reference } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ErpFundingBoardDocument,
  ErpSaveFundingIntakeMutationVariables,
  FundingCandidateInput,
  FundingOperatorInput,
  useErpChangeCandidateStatusMutation,
  useErpDeleteFundingCandidateMutation,
  useErpDeleteFundingIntakeMutation,
  useErpSaveFundingIntakeMutation,
  useErpSaveFundingOperatorMutation,
  useErpLinkCandidateToUserMutation,
  useErpResolveGeoAliasMutation,
  useErpSaveFundingCandidateMutation,
  useErpAddCandidateOperatorsMutation,
  useErpDeactivateCandidateOperatorMutation,
  useErpReactivateCandidateOperatorMutation,
  useErpSetCandidatePrimaryOperatorMutation,
  useErpUnlinkCandidateFromUserMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';

interface IFundingFieldError {
  field?: string | null;
  code?: string | null;
  message?: string | null;
}

interface IMutationResult {
  ok: boolean;
  message?: string;
  /** Id созданной строки — нужен вызывающему, чтобы открыть её карточку. */
  id?: string;
  /** Код первой бизнес-ошибки (`candidate_in_use`, …) — чтобы вызывающий
   *  мог предложить обходной путь, а не только показать текст. */
  code?: string;
  /** Ошибки сервера с полем — чтобы форма показала их ПОД полем (#129а:
   *  адрес ленты не http(s)), а не одним баннером внизу. */
  errors?: IFundingFieldError[];
}

/**
 * Код отказа удаления кандидата (#95): человеку уже писали или строка из
 * Notion. Удалять такую нельзя — только архивировать.
 */
export const CANDIDATE_IN_USE = 'candidate_in_use';

type ErrList =
  | Array<{
      field?: string | null;
      message?: string | null;
      code?: string | null;
    } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * ТЗ 0024 — запись в воронку.
 *
 * Раньше хук возвращал `{ok, message}`, а вызывающий его выбрасывал: смена
 * статуса молча откатывалась, и менеджер считал, что сохранилось. Теперь тост
 * поднимается ВНУТРИ хука — уронить его на пол больше нельзя.
 *
 * Два разных класса отказа, которые легко перепутать:
 *
 * * бизнес-ошибка приходит в `customErrors` (OutputMixin), промис при этом
 *   успешен;
 * * отказ прав — это `PermissionDenied`, он попадает в GraphQL `errors`, и при
 *   дефолтном `errorPolicy: 'none'` промис ОТКЛОНЯЕТСЯ. Без try/catch это
 *   необработанный reject в консоли и ноль обратной связи — ровно то, что
 *   получал FinanceManager.
 *
 * `refetchQueries` на доску: бейджи в таб-баре считает сервер, и без этого
 * они врали бы до перезагрузки страницы.
 */
/**
 * #141 — поля кандидата, от которых зависит подбор операторов
 * (`operatorsForCandidate`): после их правки подбор перечитывается.
 */
export const CANDIDATE_MATCH_FIELDS = [
  'employerVoivodeshipId',
  'employerPowiatId',
  'employerSameVoivodeship',
  'employmentStatus',
  'voivodeshipId',
  'powiatId',
];

/** Имя операции подбора — `ErpOperatorsForCandidate` в operations/queries. */
const OPERATORS_FOR_CANDIDATE_QUERY = 'ErpOperatorsForCandidate';

export const useFundingMutations = (onDone?: () => void) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [
    changeStatus,
    { loading: statusLoading },
  ] = useErpChangeCandidateStatusMutation();
  const [
    addOperators,
    { loading: addOperatorsLoading },
  ] = useErpAddCandidateOperatorsMutation();
  const [
    switchOffOperator,
    { loading: switchOffLoading },
  ] = useErpDeactivateCandidateOperatorMutation();
  const [
    switchOnOperator,
    { loading: switchOnLoading },
  ] = useErpReactivateCandidateOperatorMutation();
  const [
    setPrimary,
    { loading: primaryLoading },
  ] = useErpSetCandidatePrimaryOperatorMutation();
  const [
    linkUser,
    { loading: linkLoading },
  ] = useErpLinkCandidateToUserMutation();
  const [
    unlinkUser,
    { loading: unlinkLoading },
  ] = useErpUnlinkCandidateFromUserMutation();
  const [
    resolveAlias,
    { loading: aliasLoading },
  ] = useErpResolveGeoAliasMutation();
  const [
    saveCandidate,
    { loading: saveLoading },
  ] = useErpSaveFundingCandidateMutation();
  const [
    saveOperator,
    { loading: operatorSaveLoading },
  ] = useErpSaveFundingOperatorMutation();
  const [
    saveIntake,
    { loading: intakeSaveLoading },
  ] = useErpSaveFundingIntakeMutation();
  const [
    deleteIntake,
    { loading: intakeDeleteLoading },
  ] = useErpDeleteFundingIntakeMutation();
  const [
    deleteCandidate,
    { loading: candidateDeleteLoading },
  ] = useErpDeleteFundingCandidateMutation();
  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Dofinansowanie,
      });
    },
    [addToast],
  );

  const settle = useCallback(
    (
      payload:
        | { success?: boolean | null; customErrors?: ErrList }
        | null
        | undefined,
      successKey: string,
      params?: Record<string, unknown>,
    ): IMutationResult => {
      if (payload?.success) {
        notify(ToastTypes.success, t(successKey, params));
        onDone?.();
        return { ok: true };
      }
      const message = errText(payload?.customErrors);
      notify(ToastTypes.error, t('pages.dofinansowanie.toast.failed'), message);
      const errors = (
        payload?.customErrors || []
      ).filter((item): item is IFundingFieldError => Boolean(item));
      return { ok: false, message, errors };
    },
    [notify, onDone, t],
  );

  const fail = useCallback(
    (error: unknown): IMutationResult => {
      const graphQLMessage = (error as ApolloError)?.graphQLErrors?.[0]
        ?.message;
      // Текст `permissions_checker` — единственный признак отказа прав,
      // который доезжает до клиента.
      const forbidden = Boolean(graphQLMessage?.includes('permission'));
      const title = forbidden
        ? t('pages.dofinansowanie.errors.forbidden')
        : t('pages.dofinansowanie.toast.failed');
      notify(ToastTypes.error, title, forbidden ? undefined : graphQLMessage);
      return { ok: false, message: graphQLMessage };
    },
    [notify, t],
  );

  // `fundingBoard` пересчитывает бейджи и счётчики колонок. Но карточки на
  // доске приходят ПЯТЬЮ независимыми запросами `fundingCandidates` (по
  // колонке), и без их обновления карточка после переноса оставалась в
  // прежней колонке, пока цифра в шапке уже менялась. `refetchQueries` по
  // имени операции перезапрашивает все активные её варианты разом.
  const boardRefetch = [
    { query: ErpFundingBoardDocument },
    'ErpFundingCandidates',
    // Счётчик «Показано X–Y из N» — отдельный запрос под теми же фильтрами;
    // без него после смены статуса «из N» и блокировка кнопки «дальше»
    // оставались от прежней выборки.
    'ErpFundingCandidatesTotal',
  ];

  // Состав операторов меняет только АГРЕГАТЫ — плитку очереди «Без
  // оператора» и «Показано X–Y из N». Сами строки обновляет нормализованный
  // кеш из ответа мутации, поэтому `ErpFundingCandidates` здесь нет
  // намеренно (см. комментарий у `addCandidateOperators`).
  const aggregateRefetch = [
    { query: ErpFundingBoardDocument },
    'ErpFundingCandidatesTotal',
  ];

  // Набор живёт сразу на ЧЕТЫРЁХ экранах: вкладка «Наборы», карточка
  // оператора, карточка самого набора и строка в реестре операторов —
  // «Новый набор — посмотреть» там ВЫЧИСЛЯЕТСЯ из списка наборов. Список,
  // обновляющий не всё, оставляет остальные врать; реестра здесь и не
  // хватало, из-за чего после удаления набора его чип висел до перезагрузки.
  // Оператор живёт в реестре и в своей карточке; форма правки уехала на
  // уровень страницы и до `refetch` вкладки больше не дотягивается — да и не
  // должна: обновление принадлежит мутации, а не тому, кто её позвал.
  const operatorRefetch = ['ErpFundingOperators', 'ErpFundingOperator'];

  const intakeRefetch = [
    'ErpFundingIntakes',
    'ErpFundingIntakesTotal',
    'ErpFundingOperator',
    'ErpFundingOperators',
    'ErpFundingIntake',
  ];

  return {
    loading:
      statusLoading ||
      addOperatorsLoading ||
      switchOffLoading ||
      switchOnLoading ||
      primaryLoading ||
      linkLoading ||
      unlinkLoading ||
      aliasLoading ||
      saveLoading ||
      operatorSaveLoading ||
      intakeSaveLoading ||
      intakeDeleteLoading ||
      candidateDeleteLoading,

    changeCandidateStatus: useCallback(
      async (candidateId: string, status: string, note?: string) => {
        try {
          const { data } = await changeStatus({
            variables: { candidateId, status, note },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.changeCandidateStatus,
            'pages.dofinansowanie.toast.statusChanged',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [changeStatus, settle, fail],
    ),

    /**
     * Добавить кандидату операторов — сразу нескольких.
     *
     * ⚠️ Перезапрашиваются только АГРЕГАТЫ (доска и «из N»): сам кандидат
     * приезжает в ответе мутации и кладётся в нормализованный кеш, откуда
     * его перечитывают и таблица, и карточка. Слепой refetch
     * `ErpFundingCandidates` тут вреден: он бьёт по skip-нутым запросам и
     * перечитывает страницу с текущим `offset`, из-за чего строка уезжает.
     */
    addCandidateOperators: useCallback(
      async (
        candidateId: string,
        operatorIds: string[],
        note?: string,
        // Карточка оператора добавляет КАНДИДАТА — «Добавлено операторов: 1»
        // там читается наоборот, поэтому текст успеха можно подменить.
        successKey = 'pages.dofinansowanie.toast.operatorsAdded',
      ) => {
        if (!operatorIds.length) return { ok: true } as IMutationResult;
        try {
          const { data } = await addOperators({
            variables: { candidateId, operatorIds, note },
            refetchQueries: aggregateRefetch,
          });
          const payload = data?.addCandidateOperators;
          // 🔴 Число берём У СЕРВЕРА, а не из длины своего запроса. Сервер
          // не воскрешает оператора, выключенного человеком (правило
          // липкости), и молча пропускает уже активного — а тост при этом
          // радостно печатал «Добавлено операторов: 1», хотя на экране не
          // менялось ничего.
          const added = payload?.addedCount ?? 0;
          if (payload?.success && added === 0) {
            notify(
              ToastTypes.error,
              t('pages.dofinansowanie.toast.nothingAdded'),
              t('pages.dofinansowanie.toast.nothingAddedHint'),
            );
            return { ok: false };
          }
          return settle(payload, successKey, { n: added });
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [addOperators, settle, fail, notify, t],
    ),

    /** Выключить оператора у кандидата. Причина обязательна. */
    deactivateCandidateOperator: useCallback(
      async (
        candidateId: string,
        operatorId: string,
        reason: string,
        note?: string,
      ) => {
        try {
          const { data } = await switchOffOperator({
            variables: { candidateId, operatorId, reason, note },
            refetchQueries: aggregateRefetch,
          });
          return settle(
            data?.deactivateCandidateOperator,
            'pages.dofinansowanie.toast.operatorOff',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [switchOffOperator, settle, fail],
    ),

    /** Вернуть выключенного оператора в работу. */
    reactivateCandidateOperator: useCallback(
      async (candidateId: string, operatorId: string, note?: string) => {
        try {
          const { data } = await switchOnOperator({
            variables: { candidateId, operatorId, note },
            refetchQueries: aggregateRefetch,
          });
          return settle(
            data?.reactivateCandidateOperator,
            'pages.dofinansowanie.toast.operatorBack',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [switchOnOperator, settle, fail],
    ),

    /**
     * Отметить ведущего оператора (или снять пометку).
     *
     * Агрегаты не трогает: пометка не меняет ни состав активных операторов,
     * ни рассылку — только то, с кем работаем в первую очередь.
     */
    setCandidatePrimaryOperator: useCallback(
      async (candidateId: string, operatorId: string | null) => {
        try {
          const { data } = await setPrimary({
            variables: { candidateId, operatorId },
          });
          return settle(
            data?.setCandidatePrimaryOperator,
            operatorId
              ? 'pages.dofinansowanie.toast.primarySet'
              : 'pages.dofinansowanie.toast.primaryCleared',
          );
        } catch (error) {
          return fail(error);
        }
      },
      [setPrimary, settle, fail],
    ),

    linkCandidateToUser: useCallback(
      async (candidateId: string, userId: string) => {
        try {
          const { data } = await linkUser({
            variables: { candidateId, userId },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.linkCandidateToUser,
            'pages.dofinansowanie.toast.linked',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [linkUser, settle, fail],
    ),

    unlinkCandidateFromUser: useCallback(
      async (candidateId: string) => {
        try {
          const { data } = await unlinkUser({
            variables: { candidateId },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.unlinkCandidateFromUser,
            'pages.dofinansowanie.toast.unlinked',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [unlinkUser, settle, fail],
    ),

    resolveGeoAlias: useCallback(
      async (id: string, powiatId: string | null, ignore = false) => {
        try {
          const { data } = await resolveAlias({
            variables: { id, powiatId, ignore },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.resolveGeoAlias,
            'pages.dofinansowanie.toast.geoResolved',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [resolveAlias, settle, fail],
    ),

    saveFundingCandidate: useCallback(
      // `id: null` — СОЗДАНИЕ. Тип был `string`, из-за чего создать кандидата
      // этим хуком было нельзя даже из кода, хотя мутация — upsert и `$id`
      // в документе уже nullable.
      //
      // `previousOperatorIds` — активные операторы ДО сохранения. Нужны, чтобы
      // назвать того, кого привязал ID wsparcia (#96): сервер отдаёт только
      // исход, а новый состав — в `candidate.operators`.
      //
      // `input.operatorIds` (#134) — только при создании: сервер заводит
      // связь с операторами в той же транзакции. При правке непустой список
      // сервер отклонит (`invalid_input` по полю `operatorIds`).
      async (
        id: string | null,
        input: FundingCandidateInput | Record<string, unknown>,
        previousOperatorIds?: string[],
      ) => {
        try {
          const { data } = await saveCandidate({
            variables: { id, input },
            // ID wsparcia и операторы при создании меняют состав операторов —
            // а он меняет только агрегаты (плитка «Без оператора», «из N»);
            // строки обновляет нормализованный кеш из ответа.
            refetchQueries: [
              ...('supportId' in input || 'operatorIds' in input
                ? aggregateRefetch
                : []),
              // #141: география (своя и работодателя) и форма занятости
              // меняют подбор — `operatorsForCandidate` в кеше нормализован
              // не по кандидату, а по аргументу, и сам не обновится. Без
              // перечитывания «Указать работодателя» сохранялось, а подбор
              // и плашка «по месту жительства» оставались прежними.
              ...(id && CANDIDATE_MATCH_FIELDS.some((name) => name in input)
                ? [OPERATORS_FOR_CANDIDATE_QUERY]
                : []),
            ],
          });
          const payload = data?.saveFundingCandidate;
          const result = settle(payload, 'pages.dofinansowanie.toast.saved');
          if (result.ok) {
            const link = payload?.supportIdLink;
            if (link === 'linked') {
              const before = new Set(previousOperatorIds || []);
              const added = (payload?.candidate?.operators || [])
                .filter((operator) => !before.has(operator.id))
                .map((operator) => operator.name)
                .join(', ');
              notify(
                ToastTypes.success,
                added && previousOperatorIds
                  ? t('pages.dofinansowanie.supportId.toast.linked', {
                      name: added,
                      interpolation: { escapeValue: false },
                    })
                  : t('pages.dofinansowanie.supportId.toast.linkedGeneric'),
              );
            } else if (
              link === 'ambiguous' ||
              link === 'no_operator' ||
              link === 'no_prefix' ||
              link === 'switched_off'
            ) {
              // `already_linked` и пустой исход молчат: менять нечего.
              notify(
                ToastTypes.warning,
                t(`pages.dofinansowanie.supportId.toast.${link}`),
              );
            }
            // Id созданного — вызывающему, чтобы открыть его карточку.
            return { ...result, id: payload?.candidate?.id ?? undefined };
          }
          return result;
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [saveCandidate, settle, fail, notify, t],
    ),

    /**
     * Архив и возврат из архива (#95). Сервер меняет ТОЛЬКО флаг.
     *
     * Список перечитывается целиком: убрать или вернуть строку зависит от
     * фильтра «показывать архив», который правкой кеша не угадать. Список
     * постраничный (offset), а не накопительный, так что перезапрос не
     * схлопывает подгруженное.
     */
    archiveFundingCandidate: useCallback(
      async (id: string, archived: boolean) => {
        try {
          const { data } = await saveCandidate({
            variables: { id, input: { isArchived: archived } },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.saveFundingCandidate,
            archived
              ? 'pages.dofinansowanie.archive.toastArchived'
              : 'pages.dofinansowanie.archive.toastRestored',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [saveCandidate, settle, fail],
    ),

    /**
     * Удалить заявку-дубль или тестовую (#95).
     *
     * ⚠️ Не через `settle`: тот зовёт `onDone`, а у карточки это `refetch`
     * только что удалённого кандидата. Строка вычищается из ВСЕХ вариантов
     * списка правкой кеша — фильтры и страница остаются на месте; сама
     * сущность в кеше не трогается, чтобы закрывающаяся карточка не
     * перезапрашивала пустоту.
     */
    deleteFundingCandidate: useCallback(
      async (id: string): Promise<IMutationResult> => {
        try {
          const { data } = await deleteCandidate({
            variables: { id },
            refetchQueries: aggregateRefetch,
            update: (cache, { data: response }) => {
              if (!response?.deleteFundingCandidate?.success) return;
              // Сравниваем с тем `id`, которым строка лежит в кеше, — формат
              // `deletedId` у сервера свой (pk), и сверка по нему могла бы
              // промахнуться молча.
              const target = String(id);
              // ⚠️ `cache.modify` промахивается по имени поля МОЛЧА — имя
              // сверяет `candidateCachePruning.test.ts` с документом запроса.
              cache.modify({
                id: 'ROOT_QUERY',
                fields: {
                  fundingCandidates(existing, { readField }) {
                    if (!Array.isArray(existing)) return existing;
                    return (existing as Reference[]).filter(
                      (ref) => String(readField('id', ref)) !== target,
                    );
                  },
                },
              });
            },
          });
          const payload = data?.deleteFundingCandidate;
          if (payload?.success) {
            notify(
              ToastTypes.success,
              t('pages.dofinansowanie.archive.toastDeleted'),
            );
            return { ok: true };
          }
          const code = payload?.customErrors?.find((e) => e?.code)?.code;
          if (code === CANDIDATE_IN_USE) {
            notify(
              ToastTypes.error,
              t('pages.dofinansowanie.archive.inUseTitle'),
              t('pages.dofinansowanie.archive.inUseText'),
            );
            return { ok: false, code };
          }
          const message = errText(payload?.customErrors);
          notify(
            ToastTypes.error,
            t('pages.dofinansowanie.toast.failed'),
            message,
          );
          return { ok: false, message, code: code || undefined };
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [deleteCandidate, notify, fail, t],
    ),

    saveFundingOperator: useCallback(
      async (id: string | null, input: FundingOperatorInput) => {
        try {
          const { data } = await saveOperator({
            variables: { id, input },
            // С номером проекта сохранение может привязать кандидатов (#96),
            // а это меняет плитку «Без оператора» на доске.
            refetchQueries: input.projectNumber
              ? [...operatorRefetch, { query: ErpFundingBoardDocument }]
              : operatorRefetch,
          });
          const payload = data?.saveFundingOperator;
          const result = settle(payload, 'pages.dofinansowanie.toast.saved');
          if (result.ok) {
            const linked = payload?.linkedCandidatesCount ?? 0;
            if (payload?.projectNumberLink === 'ambiguous') {
              notify(
                ToastTypes.warning,
                t('pages.dofinansowanie.projectNumber.toast.ambiguous'),
                t('pages.dofinansowanie.projectNumber.toast.ambiguousHint'),
              );
            } else if (payload?.projectNumberLink === 'linked' && linked > 0) {
              // Ноль привязанных молчит: «привязано 0» на каждом сохранении
              // оператора читалось бы как поломка.
              notify(
                ToastTypes.success,
                t('pages.dofinansowanie.projectNumber.toast.linked', {
                  n: linked,
                }),
              );
            }
          }
          return result;
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [saveOperator, settle, fail, notify, t],
    ),

    saveFundingIntake: useCallback(
      async (input: ErpSaveFundingIntakeMutationVariables) => {
        try {
          const { data } = await saveIntake({
            variables: input,
            refetchQueries: intakeRefetch,
          });
          return {
            ...settle(
              data?.saveFundingIntake,
              'pages.dofinansowanie.toast.saved',
            ),
            id: data?.saveFundingIntake?.intake?.id,
          };
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [saveIntake, settle, fail],
    ),

    deleteFundingIntake: useCallback(
      async (id: string) => {
        try {
          const { data } = await deleteIntake({
            variables: { id },
            refetchQueries: intakeRefetch,
          });
          return settle(
            data?.deleteFundingIntake,
            'pages.dofinansowanie.toast.intakeDeleted',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [deleteIntake, settle, fail],
    ),
  };
};
